<div class="vendor-form-container">
  <!-- Loading Spinner -->
  @if (saving) {
    <div class="loading-overlay">
      <mat-spinner diameter="60"></mat-spinner>
      <p>Loading...</p>
    </div>
  }

  <div class="form-content">

    @if (isNetsuiteSourced) {
      <div style="margin: 0 0 16px; padding: 12px 16px; background:#fff8e1; border:1px solid #ffe082; border-radius:4px; color:#6d4c00; font-size:0.9rem;">
        🔒 This party is sourced from <strong>NetSuite</strong> and is read-only in Healthy Contracts. It is kept in sync nightly — edits must be made in NetSuite.
      </div>
    }

    <!-- Header Section -->
    <div class="header-section">
      <div class="title-section">
        @if (isEditMode) {
          <h1>Edit Party</h1>
          <p class="subtitle">Update existing party record</p>
        } @else {
          <h1>New Party</h1>
          <p class="subtitle">Create a new party record</p>
        }
        <p class="required-fields-note">Fields marked with <span class="required-indicator">*</span> are required</p>
      </div>

      <div class="header-actions">
        <button mat-stroked-button color="accent" (click)="cancelSubmit()" type="button">
          <mat-icon>close</mat-icon>
          Cancel
        </button>
        <button mat-raised-button color="primary"
                [disabled]="!form.valid || saving"
                (click)="onSubmit()" type="button">
          <mat-icon>save</mat-icon>
          Save Party
        </button>
      </div>
    </div>

    <form [formGroup]="form" (ngSubmit)="onSubmit()">

      <!-- Basic Information Section -->
      <mat-card class="form-card">
        <mat-card-header>
          <mat-card-title>
            <mat-icon>info</mat-icon>
            Basic Information
          </mat-card-title>
        </mat-card-header>
        <mat-card-content>
          <div class="form-grid">
            <!-- Vendor Number (readonly) -->
            @if (form.get('vendorNum')?.value) {
              <mat-form-field appearance="outline" class="full-width">
                <mat-label>Vendor Number</mat-label>
                <input matInput formControlName="vendorNum" readonly>
                <mat-icon matSuffix color="warn">lock</mat-icon>
              </mat-form-field>
            }

            <!-- Legal Name (stored as `name`) -->
            <mat-form-field appearance="outline">
              <mat-label>Legal Name <span class="required-indicator">*</span></mat-label>
              <input matInput formControlName="name" placeholder="Enter legal name" required>
              <mat-icon matSuffix>business</mat-icon>
              @if (form.get('name')?.hasError('required')) {
                <mat-error>Legal name is required</mat-error>
              }
            </mat-form-field>

            <!-- Accounting Company Name -->
            <mat-form-field appearance="outline">
              <mat-label>Accounting Company Name</mat-label>
              <input matInput formControlName="accountingCompanyName" placeholder="Name used in accounting / AP">
              <mat-icon matSuffix>receipt_long</mat-icon>
            </mat-form-field>

            <!-- First Name (HCP identity — required to enable Timecards) -->
            <mat-form-field appearance="outline">
              <mat-label>First Name</mat-label>
              <input matInput formControlName="firstName" placeholder="Jane">
              <mat-icon matSuffix>person</mat-icon>
              <mat-hint>Required to enable Timecards (mobile login + display name)</mat-hint>
            </mat-form-field>

            <!-- Last Name (HCP identity — required to enable Timecards) -->
            <mat-form-field appearance="outline">
              <mat-label>Last Name</mat-label>
              <input matInput formControlName="lastName" placeholder="Smith">
              <mat-icon matSuffix>person</mat-icon>
              <mat-hint>Required to enable Timecards (mobile login + display name)</mat-hint>
            </mat-form-field>

            <!-- Email (HCP identity — required to enable Timecards) -->
            <mat-form-field appearance="outline" class="span-2">
              <mat-label>Email</mat-label>
              <input matInput formControlName="email" type="email" placeholder="jane.smith@example.com">
              <mat-icon matSuffix>mail</mat-icon>
              <mat-hint>Required to enable Timecards — used to provision the HCP's mobile-app login</mat-hint>
            </mat-form-field>

            <!-- DBA -->
            <mat-form-field appearance="outline">
              <mat-label>DBA</mat-label>
              <input matInput formControlName="dba" placeholder="Doing Business As">
              <mat-icon matSuffix>store</mat-icon>
            </mat-form-field>

            <!-- Formerly Known As -->
            <mat-form-field appearance="outline">
              <mat-label>Formerly Known As (FKA)</mat-label>
              <input matInput formControlName="formallyKnownAs" placeholder="Previous name">
            </mat-form-field>

            <!-- Standing -->
            <mat-form-field appearance="outline">
              <mat-label>Standing</mat-label>
              <mat-select formControlName="standing">
                @for (standing of partyStanding; track standing) {
                  <mat-option [value]="standing">{{ standing }}</mat-option>
                }
              </mat-select>
              <mat-icon matSuffix>verified</mat-icon>
            </mat-form-field>

            <!-- Party Type -->
            <mat-form-field appearance="outline">
              <mat-label>Party Type</mat-label>
              <mat-select formControlName="partyType">
                @for (type of partyTypes; track type) {
                  <mat-option [value]="type">{{ type }}</mat-option>
                }
              </mat-select>
              <mat-icon matSuffix>category</mat-icon>
            </mat-form-field>

            <!-- Website -->
            <mat-form-field appearance="outline" class="span-2">
              <mat-label>Website</mat-label>
              <input matInput formControlName="website" placeholder="https://example.com">
              <mat-icon matSuffix>language</mat-icon>
            </mat-form-field>
          </div>
        </mat-card-content>
      </mat-card>

      <!-- Identification Numbers Section -->
      <mat-card class="form-card">
        <mat-card-header>
          <mat-card-title>
            <mat-icon>badge</mat-icon>
            Identification Numbers
          </mat-card-title>
        </mat-card-header>
        <mat-card-content>
          <div class="form-grid">
            <mat-form-field appearance="outline">
              <mat-label>GL #</mat-label>
              <input matInput formControlName="glNumber" placeholder="GL #">
            </mat-form-field>

            <mat-form-field appearance="outline">
              <mat-label>DUNS Number</mat-label>
              <input matInput formControlName="dunsNum" placeholder="9-digit DUNS">
            </mat-form-field>

            <mat-form-field appearance="outline">
              <mat-label>NPI Number</mat-label>
              <input matInput formControlName="npiNum" placeholder="10-digit NPI">
            </mat-form-field>

            <mat-form-field appearance="outline">
              <mat-label>CCN Number</mat-label>
              <input matInput formControlName="ccnNum" placeholder="CCN">
            </mat-form-field>

            <mat-form-field appearance="outline">
              <mat-label>TIN</mat-label>
              <input matInput formControlName="tin" placeholder="Tax ID Number">
            </mat-form-field>

            <mat-form-field appearance="outline">
              <mat-label>Tax Exempt Certificate #</mat-label>
              <input matInput formControlName="taxExemptNum">
            </mat-form-field>
          </div>
        </mat-card-content>
      </mat-card>

      <!-- Relationship Section -->
      <mat-card class="form-card">
        <mat-card-header>
          <mat-card-title>
            <mat-icon>account_tree</mat-icon>
            Relationships
          </mat-card-title>
        </mat-card-header>
        <mat-card-content>
          <div class="toggle-section">
            <mat-slide-toggle formControlName="isSubContractor" color="primary">
              <span class="toggle-label">Sub-Contractor</span>
            </mat-slide-toggle>

            <mat-slide-toggle formControlName="isChildOfParentParty" color="primary">
              <span class="toggle-label">Child of Parent Party</span>
            </mat-slide-toggle>
          </div>

          @if (form.get('isChildOfParentParty')?.value) {
            <div class="parent-party-section">
              <mat-form-field appearance="outline" class="full-width">
                <mat-label>Parent Party</mat-label>
                <input matInput
                       formControlName="parentVendor"
                       [matAutocomplete]="parentAuto"
                       (blur)="onParentVendorBlur()"
                       placeholder="Type to search parties">
                <mat-autocomplete #parentAuto="matAutocomplete" [displayWith]="displayParentName">
                  @for (vendor of filteredParents$ | async; track vendor.vendorNum) {
                    <mat-option [value]="vendor.vendorNum">
                      {{ vendor | partyLabel }}
                    </mat-option>
                  }
                </mat-autocomplete>
                <mat-icon matSuffix>business_center</mat-icon>
              </mat-form-field>
            </div>
          }
        </mat-card-content>
      </mat-card>

      <!-- Primary Address Section -->
      <mat-card class="form-card">
        <mat-card-header>
          <mat-card-title>
            <mat-icon>location_on</mat-icon>
            Primary Address
          </mat-card-title>
        </mat-card-header>
        <mat-card-content>
          <div class="form-grid">
            <mat-form-field appearance="outline" class="span-2">
              <mat-label>Street Address <span class="required-indicator">*</span></mat-label>
              <input matInput formControlName="address" placeholder="123 Main Street" required>
              <mat-icon matSuffix>home</mat-icon>
              @if (form.get('address')?.hasError('required')) {
                <mat-error>Address is required</mat-error>
              }
            </mat-form-field>

            <mat-form-field appearance="outline">
              <mat-label>City <span class="required-indicator">*</span></mat-label>
              <input matInput formControlName="city" placeholder="City" required>
              @if (form.get('city')?.hasError('required')) {
                <mat-error>City is required</mat-error>
              }
            </mat-form-field>

            <mat-form-field appearance="outline">
              <mat-label>State</mat-label>
              <input matInput formControlName="state" [matAutocomplete]="stateAuto" placeholder="Select state">
              <mat-autocomplete #stateAuto="matAutocomplete">
                @for (state of filteredStates$ | async; track state) {
                  <mat-option [value]="state">{{ state }}</mat-option>
                }
              </mat-autocomplete>
            </mat-form-field>

            <mat-form-field appearance="outline">
              <mat-label>Zip Code</mat-label>
              <input matInput formControlName="zip" placeholder="12345">
            </mat-form-field>
          </div>
        </mat-card-content>
      </mat-card>

      <!-- Secondary Address Section -->
      <mat-card class="form-card">
        <mat-card-header>
          <mat-card-title>
            <mat-icon>add_location</mat-icon>
            Secondary Address
          </mat-card-title>
        </mat-card-header>
        <mat-card-content>
          <div class="form-grid">
            <mat-form-field appearance="outline" class="span-2">
              <mat-label>Street Address</mat-label>
              <input matInput formControlName="secondaryAddress" placeholder="123 Second Street">
            </mat-form-field>

            <mat-form-field appearance="outline">
              <mat-label>City</mat-label>
              <input matInput formControlName="secondaryCity" placeholder="City">
            </mat-form-field>

            <mat-form-field appearance="outline">
              <mat-label>State</mat-label>
              <input matInput formControlName="secondaryState" [matAutocomplete]="secondaryStateAuto" placeholder="Select state">
              <mat-autocomplete #secondaryStateAuto="matAutocomplete">
                @for (state of filteredSecondaryStates$ | async; track state) {
                  <mat-option [value]="state">{{ state }}</mat-option>
                }
              </mat-autocomplete>
            </mat-form-field>

            <mat-form-field appearance="outline">
              <mat-label>Zip Code</mat-label>
              <input matInput formControlName="secondaryZip" placeholder="12345">
            </mat-form-field>
          </div>
        </mat-card-content>
      </mat-card>

      <!-- Contact Information Section -->
      <mat-card class="form-card">
        <mat-card-header>
          <mat-card-title>
            <mat-icon>phone</mat-icon>
            Contact Information
          </mat-card-title>
        </mat-card-header>
        <mat-card-content>
          <div class="form-grid">
            <mat-form-field appearance="outline">
              <mat-label>Primary Phone</mat-label>
              <input matInput formControlName="phonePrimary" placeholder="(555) 123-4567">
              <mat-icon matSuffix>call</mat-icon>
            </mat-form-field>

            <mat-form-field appearance="outline">
              <mat-label>Secondary Phone</mat-label>
              <input matInput formControlName="phoneSecondary" placeholder="(555) 987-6543">
              <mat-icon matSuffix>phone</mat-icon>
            </mat-form-field>

            <mat-form-field appearance="outline">
              <mat-label>Fax</mat-label>
              <input matInput formControlName="fax" placeholder="(555) 123-4567">
              <mat-icon matSuffix>fax</mat-icon>
            </mat-form-field>
          </div>
        </mat-card-content>
      </mat-card>

      <!-- Team Assignment Section -->
      <mat-card class="form-card">
        <mat-card-header>
          <mat-card-title>
            <mat-icon>people</mat-icon>
            Team Assignment
          </mat-card-title>
        </mat-card-header>
        <mat-card-content>
          <div class="form-grid">
            <mat-form-field appearance="outline">
              <mat-label>Owner</mat-label>
              <input type="text" matInput
                formControlName="owner"
                [matAutocomplete]="ownerAuto"
                (input)="filterUsers($event)"
                placeholder="Select owner">
              <mat-icon matSuffix>person</mat-icon>
              <mat-autocomplete #ownerAuto="matAutocomplete" [displayWith]="displayUserFn">
                @for (user of filteredUsers; track user.email) {
                  <mat-option [value]="user">
                    <span class="user-option">{{ user.shortFriendlyName }}</span>
                  </mat-option>
                } @empty {
                  <mat-option disabled>No users found</mat-option>
                }
              </mat-autocomplete>
            </mat-form-field>

            <mat-form-field appearance="outline">
              <mat-label>Assessor</mat-label>
              <input type="text" matInput
                formControlName="assessor"
                [matAutocomplete]="assessorAuto"
                (input)="filterUsers($event)"
                placeholder="Select assessor">
              <mat-icon matSuffix>assignment</mat-icon>
              <mat-autocomplete #assessorAuto="matAutocomplete" [displayWith]="displayUserFn">
                @for (user of filteredUsers; track user.email) {
                  <mat-option [value]="user">
                    <span class="user-option">{{ user.friendlyName }}</span>
                  </mat-option>
                } @empty {
                  <mat-option disabled>No users found</mat-option>
                }
              </mat-autocomplete>
            </mat-form-field>

            <mat-form-field appearance="outline">
              <mat-label>Final Reviewer</mat-label>
              <input type="text" matInput
                formControlName="finalReviewer"
                [matAutocomplete]="finalReviewerAuto"
                (input)="filterUsers($event)"
                placeholder="Select reviewer">
              <mat-icon matSuffix>check_circle</mat-icon>
              <mat-autocomplete #finalReviewerAuto="matAutocomplete" [displayWith]="displayUserFn">
                @for (user of filteredUsers; track user.email) {
                  <mat-option [value]="user">
                    <span class="user-option">{{ user.friendlyName }}</span>
                  </mat-option>
                } @empty {
                  <mat-option disabled>No users found</mat-option>
                }
              </mat-autocomplete>
            </mat-form-field>
          </div>
        </mat-card-content>
      </mat-card>

      <!-- Organization Details Section -->
      <mat-card class="form-card">
        <mat-card-header>
          <mat-card-title>
            <mat-icon>corporate_fare</mat-icon>
            Organization Details
          </mat-card-title>
        </mat-card-header>
        <mat-card-content>
          <div class="form-grid">
            <mat-form-field appearance="outline">
              <mat-label>Annual Revenues</mat-label>
              <input matInput formControlName="annualRevenues" placeholder="$1,000,000">
              <mat-icon matSuffix>attach_money</mat-icon>
            </mat-form-field>

            <mat-form-field appearance="outline">
              <mat-label>Number of Employees</mat-label>
              <input matInput formControlName="numEmployees" type="number" placeholder="100">
              <mat-icon matSuffix>groups</mat-icon>
            </mat-form-field>

            <mat-form-field appearance="outline">
              <mat-label>State of Incorporation</mat-label>
              <input matInput formControlName="stateOfIncorporation" [matAutocomplete]="stateOfIncAuto" placeholder="Select state">
              <mat-autocomplete #stateOfIncAuto="matAutocomplete">
                @for (state of filteredStatesOfIncorporation$ | async; track state) {
                  <mat-option [value]="state">{{ state }}</mat-option>
                }
              </mat-autocomplete>
            </mat-form-field>

            <mat-form-field appearance="outline">
              <mat-label>Public or Private</mat-label>
              <input matInput formControlName="publicOrPrivate" placeholder="Public/Private">
            </mat-form-field>

            <mat-form-field appearance="outline">
              <mat-label>County</mat-label>
              <input matInput formControlName="county" placeholder="County name">
            </mat-form-field>

            <mat-form-field appearance="outline">
              <mat-label>Country</mat-label>
              <input matInput formControlName="country" placeholder="USA">
            </mat-form-field>

            <mat-form-field appearance="outline">
              <mat-label>Non-Profit or For Profit</mat-label>
              <input matInput formControlName="nonOrForProfit" placeholder="For Profit/Non-Profit">
            </mat-form-field>

            <mat-form-field appearance="outline" class="span-2">
              <mat-label>Specialty</mat-label>
              <mat-select formControlName="specialty" multiple>
                @for (element of specialties; track element) {
                  <mat-option [value]="element">{{ element }}</mat-option>
                }
              </mat-select>
              <mat-icon matSuffix>local_hospital</mat-icon>
            </mat-form-field>
          </div>
        </mat-card-content>
      </mat-card>

      <!-- Additional Details Section -->
      <mat-card class="form-card">
        <mat-card-header>
          <mat-card-title>
            <mat-icon>description</mat-icon>
            Additional Details
          </mat-card-title>
        </mat-card-header>
        <mat-card-content>
          <mat-form-field appearance="outline" class="full-width">
            <mat-label>Description</mat-label>
            <textarea matInput
                      formControlName="description"
                      rows="4"
                      placeholder="Enter party description"></textarea>
          </mat-form-field>

          <div class="checkbox-group">
            <mat-checkbox formControlName="baaRequired" color="primary">
              <span class="checkbox-label">Missing BAA</span>
            </mat-checkbox>

            <mat-checkbox formControlName="coiRequired" color="primary">
              <span class="checkbox-label">Missing COI</span>
            </mat-checkbox>
          </div>
        </mat-card-content>
      </mat-card>

      <!-- Attachments and Dates Section -->
      <mat-card class="form-card">
        <mat-card-header>
          <mat-card-title>
            <mat-icon>attach_file</mat-icon>
            Attachments & Important Dates
          </mat-card-title>
        </mat-card-header>
        <mat-card-content>
          <div class="action-buttons">
            <button mat-stroked-button type="button" color="primary">
              <mat-icon>add</mat-icon>
              Add New Attachment
            </button>

            <button mat-stroked-button
                    type="button"
                    color="primary"
                    (click)="openAllAttachmentsInDialog()">
              <mat-icon>folder_open</mat-icon>
              Add Existing Attachment
            </button>

            <button mat-stroked-button
                    type="button"
                    color="primary"
                    (click)="openCriticalDateDialog(currentParty.vendorNum, EntityType.Party, currentParty)">
              <mat-icon>event</mat-icon>
              Add Important Date
            </button>
          </div>

          @if (stringOfAttachmentNames) {
            <div class="attachments-list">
              <p class="attachments-label">Current Attachments:</p>
              <p class="attachments-text">{{ stringOfAttachmentNames }}</p>
            </div>
          }
        </mat-card-content>
      </mat-card>

      <!-- Integrations Section -->
      <mat-card class="form-card">
        <mat-card-header>
          <mat-card-title>
            <mat-icon>sync</mat-icon>
            Integrations
          </mat-card-title>
        </mat-card-header>
        <mat-card-content>
          <!-- Tax & Compliance Fields -->
          <h3 class="subsection-title">Tax & Compliance</h3>
          <div class="form-grid">
            <mat-form-field appearance="outline">
              <mat-label>FEIN</mat-label>
              <input matInput formControlName="fein" placeholder="XX-XXXXXXX">
              <mat-icon matSuffix>numbers</mat-icon>
            </mat-form-field>

            <mat-form-field appearance="outline">
              <mat-label>G/L Account Number</mat-label>
              <input matInput formControlName="glAccountNumber" placeholder="G/L Account #">
              <mat-icon matSuffix>account_balance</mat-icon>
            </mat-form-field>

            <mat-form-field appearance="outline">
              <mat-label>Business Structure</mat-label>
              <mat-select formControlName="businessStructure">
                <mat-option value="">-- Select --</mat-option>
                @for (structure of businessStructures; track structure) {
                  <mat-option [value]="structure">{{ structure }}</mat-option>
                }
              </mat-select>
              <mat-icon matSuffix>business</mat-icon>
            </mat-form-field>

            <mat-form-field appearance="outline">
              <mat-label>License Type</mat-label>
              <input matInput formControlName="licenseType" placeholder="License type">
              <mat-icon matSuffix>card_membership</mat-icon>
            </mat-form-field>

            <mat-form-field appearance="outline">
              <mat-label>License Number</mat-label>
              <input matInput formControlName="licenseNumber" placeholder="License #">
              <mat-icon matSuffix>badge</mat-icon>
            </mat-form-field>

            <mat-form-field appearance="outline">
              <mat-label>License State</mat-label>
              <input matInput formControlName="licenseState" placeholder="State">
              <mat-icon matSuffix>public</mat-icon>
            </mat-form-field>

            <mat-form-field appearance="outline">
              <mat-label>Diversity Standing</mat-label>
              <mat-select formControlName="diversityStanding" multiple>
                @for (diversity of diversityStandingOptions; track diversity) {
                  <mat-option [value]="diversity">{{ diversity }}</mat-option>
                }
              </mat-select>
              <mat-icon matSuffix>diversity_3</mat-icon>
            </mat-form-field>

            <mat-form-field appearance="outline">
              <mat-label>1099 Category</mat-label>
              <mat-select formControlName="category1099">
                <mat-option value="">-- Select --</mat-option>
                @for (category of categories1099; track category) {
                  <mat-option [value]="category">{{ category }}</mat-option>
                }
              </mat-select>
              <mat-icon matSuffix>receipt</mat-icon>
            </mat-form-field>

            <mat-form-field appearance="outline">
              <mat-label>Payment Terms</mat-label>
              <input matInput formControlName="paymentTerms" placeholder="Payment terms">
              <mat-icon matSuffix>schedule</mat-icon>
            </mat-form-field>
          </div>

          <div class="checkbox-group integrations-checkboxes">
            <mat-checkbox formControlName="de542" color="primary">
              <span class="checkbox-label">DEA 542</span>
            </mat-checkbox>

            <mat-checkbox formControlName="oigScreening" color="primary">
              <span class="checkbox-label">OIG Screening</span>
            </mat-checkbox>

            <mat-checkbox formControlName="ancillary" color="primary">
              <span class="checkbox-label">Ancillary</span>
            </mat-checkbox>

            <mat-checkbox formControlName="is1099Eligible" color="primary">
              <span class="checkbox-label">1099 Eligible</span>
            </mat-checkbox>
          </div>

          <!-- W9 Address Fields -->
          <h3 class="subsection-title">W9 Address</h3>
          <div class="form-grid">
            <mat-form-field appearance="outline" class="span-2">
              <mat-label>W9 Street Address 1</mat-label>
              <input matInput formControlName="w9StreetAddress1" placeholder="123 Main Street">
              <mat-icon matSuffix>home</mat-icon>
            </mat-form-field>

            <mat-form-field appearance="outline" class="span-2">
              <mat-label>W9 Street Address 2</mat-label>
              <input matInput formControlName="w9StreetAddress2" placeholder="Suite 100">
            </mat-form-field>

            <mat-form-field appearance="outline">
              <mat-label>W9 City</mat-label>
              <input matInput formControlName="w9City" placeholder="City">
            </mat-form-field>

            <mat-form-field appearance="outline">
              <mat-label>W9 State</mat-label>
              <input matInput formControlName="w9State" [matAutocomplete]="w9StateAuto" placeholder="Select state">
              <mat-autocomplete #w9StateAuto="matAutocomplete">
                @for (state of filteredW9States$ | async; track state) {
                  <mat-option [value]="state">{{ state }}</mat-option>
                }
              </mat-autocomplete>
            </mat-form-field>

            <mat-form-field appearance="outline">
              <mat-label>W9 ZIP</mat-label>
              <input matInput formControlName="w9Zip" placeholder="12345">
            </mat-form-field>
          </div>

          <!-- Invoice Remittance Address Fields -->
          <h3 class="subsection-title">Invoice Remittance Address</h3>
          <div class="form-grid">
            <mat-form-field appearance="outline" class="span-2">
              <mat-label>Invoice Remittance Street Address 1</mat-label>
              <input matInput formControlName="remittanceStreetAddress1" placeholder="123 Main Street">
              <mat-icon matSuffix>home</mat-icon>
            </mat-form-field>

            <mat-form-field appearance="outline" class="span-2">
              <mat-label>Invoice Remittance Street Address 2</mat-label>
              <input matInput formControlName="remittanceStreetAddress2" placeholder="Suite 100">
            </mat-form-field>

            <mat-form-field appearance="outline">
              <mat-label>Invoice Remittance City</mat-label>
              <input matInput formControlName="remittanceCity" placeholder="City">
            </mat-form-field>

            <mat-form-field appearance="outline">
              <mat-label>Invoice Remittance State</mat-label>
              <input matInput formControlName="remittanceState" [matAutocomplete]="remittanceStateAuto" placeholder="Select state">
              <mat-autocomplete #remittanceStateAuto="matAutocomplete">
                @for (state of filteredRemittanceStates$ | async; track state) {
                  <mat-option [value]="state">{{ state }}</mat-option>
                }
              </mat-autocomplete>
            </mat-form-field>

            <mat-form-field appearance="outline">
              <mat-label>Invoice Remittance ZIP</mat-label>
              <input matInput formControlName="remittanceZip" placeholder="12345">
            </mat-form-field>

            <mat-form-field appearance="outline" class="span-2">
              <mat-label>Remit To</mat-label>
              <input matInput formControlName="printCheckAs" placeholder="Payee name">
              <mat-icon matSuffix>payments</mat-icon>
            </mat-form-field>
          </div>
        </mat-card-content>
      </mat-card>

      <!-- Form Actions -->
      <div class="form-actions">
        <button mat-stroked-button color="accent" type="button" (click)="cancelSubmit()">
          <mat-icon>close</mat-icon>
          Cancel
        </button>
        <button mat-raised-button color="primary"
                type="submit"
                [disabled]="!form.valid || saving">
          <mat-icon>save</mat-icon>
          Save Party
        </button>
      </div>

    </form>
  </div>
</div>
